import React, { useState, useEffect } from 'react';
import { getToken } from '../services/api';
import LoadingScreen from './LoadingScreen';
import styles from '../styles/DevDashboard.module.css';

/**
 * DevDashboard Component
 * Dashboard for viewing all conversation logs in development mode
 * Only accessible when logged in with DEV_PASSWORD
 *
 * Props:
 * - onBack: Callback to navigate back to main app
 */
function DevDashboard({ onBack }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    loadStats();
  }, []);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dev/conversations?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversations: ${response.statusText}`);
      }

      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('[DevDashboard] Error loading conversations:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dev/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load stats: ${response.statusText}`);
      }

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      console.error('[DevDashboard] Error loading stats:', err);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setError(null);

      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dev/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load messages: ${response.statusText}`);
      }

      const data = await response.json();
      setMessages(data.messages || []);
      setSelectedConversation(conversationId);
    } catch (err) {
      console.error('[DevDashboard] Error loading messages:', err);
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatMessageContent = (content) => {
    // Strip MI tags and behavioral tags for cleaner display
    return content
      .replace(/&[^&]*&/g, '')  // Remove &tag&
      .replace(/{{[^}]*}}/g, '') // Remove {{tag}}
      .trim();
  };

  const downloadConversation = () => {
    if (!selectedConversation) return;

    // Find the conversation details
    const conversation = conversations.find(c => c.id === selectedConversation);
    if (!conversation) return;

    // Format as human-readable text
    let textContent = '';

    // Header section
    textContent += '========================================\n';
    textContent += 'CONVERSATION EXPORT\n';
    textContent += '========================================\n\n';

    textContent += `Conversation ID: ${conversation.id}\n`;
    textContent += `User: ${conversation.user_name || conversation.user_email}\n`;
    textContent += `Email: ${conversation.user_email}\n`;
    if (conversation.objective_text) {
      textContent += `Objective: ${conversation.objective_text}\n`;
    }
    textContent += `Created: ${formatDate(conversation.created_at)}\n`;
    textContent += `Last Updated: ${formatDate(conversation.updated_at)}\n`;
    textContent += `Status: ${conversation.is_active ? 'Active' : 'Inactive'}\n`;
    textContent += `Total Messages: ${messages.length}\n`;
    textContent += `Exported: ${formatDate(new Date().toISOString())}\n`;

    textContent += '\n========================================\n';
    textContent += 'MESSAGES\n';
    textContent += '========================================\n\n';

    // Messages section
    messages.forEach((msg, index) => {
      const speaker = msg.role === 'user' ? 'User' : 'Sophia';
      textContent += `--- ${speaker}: ${formatDate(msg.timestamp)} ---\n`;
      textContent += `${msg.content}\n`;
      textContent += `(RAW: ${msg.content})\n`;
      textContent += '\n';
    });

    textContent += '\n========================================\n';
    textContent += 'END OF CONVERSATION\n';
    textContent += '========================================\n';

    // Create blob and download
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation_${selectedConversation}_${conversation.user_email}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter conversations by search query
  const filteredConversations = conversations.filter(conv => {
    const searchLower = searchQuery.toLowerCase();
    const userEmail = (conv.user_email || '').toLowerCase();
    const userName = (conv.user_name || '').toLowerCase();
    const objective = (conv.objective_text || '').toLowerCase();

    return userEmail.includes(searchLower) ||
           userName.includes(searchLower) ||
           objective.includes(searchLower);
  });

  if (isLoading) {
    return <LoadingScreen text="Loading dev dashboard..." variant="dots" />;
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButton} aria-label="Back">
          ← Back
        </button>
        <h1 className={styles.title}>Dev Dashboard</h1>
        <div className={styles.env}>Development Mode</div>
      </div>

      {/* Stats */}
      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.total_users || 0}</div>
            <div className={styles.statLabel}>Total Users</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.total_conversations || 0}</div>
            <div className={styles.statLabel}>Conversations</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.total_messages || 0}</div>
            <div className={styles.statLabel}>Messages</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.active_users_7d || 0}</div>
            <div className={styles.statLabel}>Active (7d)</div>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* Conversations List */}
        <div className={styles.conversationsPanel}>
          <div className={styles.panelHeader}>
            <h2>Conversations ({filteredConversations.length})</h2>
          </div>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search by user or objective..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <div className={styles.conversationsList}>
            {filteredConversations.length === 0 ? (
              <p className={styles.emptyText}>No conversations found.</p>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`${styles.conversationCard} ${selectedConversation === conv.id ? styles.selected : ''}`}
                  onClick={() => loadMessages(conv.id)}
                >
                  <div className={styles.conversationHeader}>
                    <div className={styles.conversationUser}>
                      {conv.user_name || conv.user_email}
                    </div>
                    <div className={styles.conversationDate}>
                      {formatDate(conv.updated_at)}
                    </div>
                  </div>
                  {conv.objective_text && (
                    <div className={styles.conversationObjective}>
                      Goal: {conv.objective_text}
                    </div>
                  )}
                  <div className={styles.conversationMeta}>
                    <span className={styles.messageCount}>{conv.message_count || 0} messages</span>
                    {conv.is_active && <span className={styles.activeBadge}>Active</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages Panel */}
        <div className={styles.messagesPanel}>
          <div className={styles.panelHeader}>
            <h2>Messages</h2>
            {selectedConversation && (
              <button
                onClick={downloadConversation}
                className={styles.downloadButton}
                title="Download conversation as JSON"
              >
                ⬇ Download
              </button>
            )}
          </div>

          {selectedConversation === null ? (
            <div className={styles.emptyState}>
              <p>Select a conversation to view messages</p>
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No messages in this conversation</p>
            </div>
          ) : (
            <div className={styles.messagesList}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}
                >
                  <div className={styles.messageHeader}>
                    <span className={styles.messageRole}>
                      {msg.role === 'user' ? '👤 User' : '🤖 Assistant'}
                    </span>
                    <span className={styles.messageTimestamp}>
                      {formatDate(msg.timestamp)}
                    </span>
                  </div>
                  <div className={styles.messageContent}>
                    {formatMessageContent(msg.content)}
                  </div>

                  {/* Show raw content in a collapsible section if it has tags */}
                  {(msg.content.includes('&') || msg.content.includes('{{') || msg.content.includes('<')) && (
                    <details className={styles.rawContent}>
                      <summary>View Raw Content</summary>
                      <pre>{msg.content}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DevDashboard;
